import { Grid, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import { SpinnerIndicator } from "components/CallServer";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiSelectFilter from "components/MuiExtras/MuiSelectFilter";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { TextPluralization } from "components/TextPluralization";
import UsernameInspect from "components/UsernameInspect";
import UserProfileImageLink from "components/UserProfileImageLink";
import LibUtils from "lib/LibUtils";
import { KnowledgeTestDifficultyLevel, MemberType } from "models/ApiEnums";
import { DashboardGraphByGroupModel, DashboardKnowledgeIndicatorsDataModel, DashboardKnowledgeIndicatorsDetailColumn, DashboardKnowledgeIndicatorsDetailModel, DashboardKnowledgeIndicatorsGraphGroup, DashboardKnowledgeIndicatorsSummaryColumn, DashboardUserAndGroupsAndManagersBaseFilter, PagedResult } from "models/ApiModels";
import * as React from "react";
import { WithTranslation } from "react-i18next";
import { GraphLevels, KnowledgeListOptionsType, KnowledgeTestHGraph, KnowledgeTotalColumn, KnowlegeDataModelLine } from "views/Dashboards/components/KnowledgeCommons";
import { RenderTopicModelListEvents } from "views/Dashboards/components/RenderTopicModelList";
import { RenderUserModelListEvents } from "views/Dashboards/components/RenderUserModelList";
import KnowledgeTestIndicatorsDrawer from "views/Dashboards/KnowledgeTestDashboard/KnowledgeTestIndicatorsDrawer";
import { getTopicSecondViewAction, getTopicUrl } from "views/Timeline/Components/FeedHelpers";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    defaultGraphGroup: DashboardKnowledgeIndicatorsGraphGroup;
    tile: DashboardKnowledgeIndicatorsSummaryColumn;
    filter: DashboardUserAndGroupsAndManagersBaseFilter;
    listOptions: KnowledgeListOptionsType[];
}

interface IGraphState {
    data: DashboardGraphByGroupModel;
    loading: boolean;
    lastRequest: Date;
}

type GraphProps = IGraphProps & WithTranslation;

export class KnowledgeTestIndicatorsGraph extends React.PureComponent<GraphProps, IGraphState>{

    private selectedBarIndex: number = null;

    constructor(props: GraphProps) {
        super(props);

        this.state = {
            data: null,
            loading: true,
            lastRequest: new Date()
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.KnowledgeTest.IndicatorsGraph." + r);
    }

    ///
    private loadData() {

        api.getDashboardKnowledgeTestIndicators({
            defaultGraphGroup: this.props.defaultGraphGroup,
            columnToSummary: this.props.tile,
            detailColumn: DashboardKnowledgeIndicatorsDetailColumn.Graph,
            ...this.props.filter,
            range: null,
        })
            .then((result) => {
                this.setState({ data: (result as DashboardGraphByGroupModel), loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    private checkForUpdateState(prevProps: GraphProps) {
        if (this.props.tile != prevProps.tile
            || !LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.selectedBarIndex = null;
            this.setState({ loading: true }, () => this.loadData());
        }
    }

    private isGroupedByUser() {
        const { defaultGraphGroup, filter: { memberId, topicId } } = this.props;
        return (defaultGraphGroup == DashboardKnowledgeIndicatorsGraphGroup.User && ((memberId == null && topicId == null) || memberId == null))
            || (defaultGraphGroup == DashboardKnowledgeIndicatorsGraphGroup.Topic && ((memberId == null && topicId != null) || topicId != null))
            ;
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public componentDidUpdate(prevProps: GraphProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {
        const { tile } = this.props;
        const { data, loading } = this.state;
        const groupByUser = this.isGroupedByUser();

        let title = "";
        let subtitle = "";
        switch (tile) {
            case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
                title = groupByUser ? this.res("KnowledgeTitleUsers") : this.res("KnowledgeTitleTopics");
                subtitle = this.res("KnowledgeSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Result:
                title = groupByUser ? this.res("ResultTitleUsers") : this.res("ResultTitleTopics");
                subtitle = this.res("ResultSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Trust:
                title = groupByUser ? this.res("TrustTitleUsers") : this.res("TrustTitleTopics");
                subtitle = this.res("TrustSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Completed:
                title = groupByUser ? this.res("CompletedTitleUsers") : this.res("CompletedTitleTopics");
                subtitle = this.res("CompletedSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Revision:
                title = groupByUser ? this.res("RevisionTitleUsers") : this.res("RevisionTitleTopics");
                subtitle = this.res("RevisionSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Engagement:
                title = this.res("EngagementTitle");
                subtitle = this.res("EngagementSubtitle");
        }

        return (
            <>
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography component="p" variant="h6"><b>{title.toString().toLocaleUpperCase()}</b></Typography>
                            <MuiMuted><small>{subtitle}</small></MuiMuted>
                        </Grid>
                    </Grid>
                </MuiContainer>


                {
                    loading
                        ? <SpinnerIndicator />
                        : data == null
                            ? <AppMessageBox resource="LoadGraphDataFailed" />
                            : (
                                <>
                                    <KnowledgeTestHGraph
                                        tile={tile}
                                        data={data}
                                        onBarSelected={
                                            (index) => {
                                                this.selectedBarIndex = index;
                                                this.setState({ lastRequest: new Date() });
                                            }
                                        }
                                    />

                                    <KnowledgeTestIndicatorsList
                                        {...this.props}
                                        tile={tile}
                                        selectedBar={this.selectedBarIndex}
                                        isGroupedByUser={this.isGroupedByUser()}
                                        lastRequest={this.state.lastRequest}
                                    />
                                </>
                            )
                }
            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<any>;
    offset: number;
    loading: boolean;
    primaryListOption: DashboardKnowledgeIndicatorsDetailColumn;
    secondaryListOption: number;
}

export interface IListProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    defaultGraphGroup: DashboardKnowledgeIndicatorsGraphGroup;
    tile: DashboardKnowledgeIndicatorsSummaryColumn;
    filter: DashboardUserAndGroupsAndManagersBaseFilter;
    selectedBar?: number;
    listOptions: KnowledgeListOptionsType[];
    isGroupedByUser: boolean;
    lastRequest: Date;
}

type ListProps = IListProps & WithTranslation;

const IndicatorListPrefix = "Views.Dashboards.KnowledgeTest.IndicatorsList";

export class KnowledgeTestIndicatorsList extends React.PureComponent<ListProps, IListState> {

    constructor(props: ListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            ...this.computeListOptions()
        };
    }


    ///
    private res(r: string, opt?: any) {
        return this.props.t(IndicatorListPrefix + "." + r, opt);
    }

    ///
    private loadData(offset: number) {

        this.setState({ loading: true });

        api.getDashboardKnowledgeTestIndicators(
            {
                defaultGraphGroup: this.props.defaultGraphGroup,
                columnToSummary: this.props.tile,
                detailColumn: this.state.primaryListOption,
                selectedValue: this.state.secondaryListOption,
                drillDownId: null,
                drillDownSearchText: null,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
                ...this.props.filter
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private handleListOption = (e: SelectChangeEvent<DashboardKnowledgeIndicatorsDetailColumn>) => {
        var g = GraphLevels(this.props.i18n);
        this.setState({
            primaryListOption: e.target.value as DashboardKnowledgeIndicatorsDetailColumn,

            secondaryListOption:
                e.target.value == DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel
                    || e.target.value == DashboardKnowledgeIndicatorsDetailColumn.UserLevel
                    || e.target.value == DashboardKnowledgeIndicatorsDetailColumn.UserQt
                    || e.target.value == DashboardKnowledgeIndicatorsDetailColumn.TopicLevel
                    || e.target.value == DashboardKnowledgeIndicatorsDetailColumn.TopicQt
                    || e.target.value == DashboardKnowledgeIndicatorsDetailColumn.UserParticipateLevel
                    ? g.find(p => p.column == this.props.tile).options[0].key
                    : null,

            loading: true
        }, () => this.loadData(0));
    };

    ///
    private handleSecondaryListOption = (e: SelectChangeEvent<number>) => {
        this.setState({ secondaryListOption: e.target.value as number }, () => this.loadData(0));
    };

    ///
    private getPrimaryListOptions() {
        var options = this.props.listOptions.find(p => p.column == this.props.tile).options;

        if (this.props.filter.memberType == MemberType.User) {
            options = options.filter(p => p.group == "singleUser" || p.group == "");
        }
        if (this.props.filter.topicId != null) {
            options = options.filter(p => p.group != "anyUser");
        }
        if (this.props.filter.memberType == null || this.props.filter.memberType == MemberType.Group) {
            options = options.filter(p => p.group != "singleUser");
        }

        options = options.filter(p => p.graph != (this.props.isGroupedByUser ? "topic" : "user"));

        return options;
    }

    ///
    private getSecondaryListOptions() {
        var filter = GraphLevels(this.props.i18n).find(p => p.column == this.props.tile);
        return filter.options;
    }

    ///
    private computeListOptions() {
        let tiles = this.getPrimaryListOptions();
        if (this.props.selectedBar != null) {
            tiles = tiles.filter(p => p.useLevel);
        }

        return { primaryListOption: tiles[0].key, secondaryListOption: this.props.selectedBar };
    }

    ///
    private drillDown(id: string) {
        this.setState({
            dialog: <KnowledgeTestIndicatorsDrawer
                {...this.props}
                summaryColumn={this.props.tile}
                detailColumn={this.state.primaryListOption}
                selectedValue={this.state.secondaryListOption}
                drillDownId={id}
                filter={this.props.filter}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    }

    ///
    private getEmptyMessage() {
        switch (this.state.primaryListOption) {
            case DashboardKnowledgeIndicatorsDetailColumn.UserBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserWorst:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
                return this.props.tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
                    ? this.res("EmptyKnowledge")
                    : this.props.tile == DashboardKnowledgeIndicatorsSummaryColumn.Completed
                        ? this.res("EmptyCompleted")
                        : this.props.tile == DashboardKnowledgeIndicatorsSummaryColumn.Trust
                            ? this.res("EmptyTrust")
                            : this.props.tile == DashboardKnowledgeIndicatorsSummaryColumn.Result
                                ? this.res("EmptyResult")
                                : this.res("EmptyRevision");

            case DashboardKnowledgeIndicatorsDetailColumn.UserLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.UserQt:
                return this.res("EmptyUserLevel");

            case DashboardKnowledgeIndicatorsDetailColumn.TopicLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicQt:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel:
                return this.res("EmptyTopicLevel");

            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst:
                return this.res("EmptyUserTopicData");

            case DashboardKnowledgeIndicatorsDetailColumn.UserParticipate:
                return this.res("EmptyUserParticipate");

            case DashboardKnowledgeIndicatorsDetailColumn.UserParticipateLevel:
                return this.res("EmptyUserParticipateLevel");
        }
        return null;
    }

    ///
    private getRightColumnName() {
        const { tile } = this.props;
        const { primaryListOption } = this.state;
        var rightColumnName = null;

        switch (primaryListOption) {

            case DashboardKnowledgeIndicatorsDetailColumn.UserBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserWorst:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
                rightColumnName = tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
                    ? this.res("KnowledgeAvgText")
                    : tile == DashboardKnowledgeIndicatorsSummaryColumn.Completed
                        ? this.res("CompletedAvgText")
                        : rightColumnName = tile == DashboardKnowledgeIndicatorsSummaryColumn.Result
                            ? this.res("ResultAvgText")
                            : tile == DashboardKnowledgeIndicatorsSummaryColumn.Trust
                                ? this.res("TrustAvgText")
                                : tile == DashboardKnowledgeIndicatorsSummaryColumn.Revision
                                    ? this.res("RevisionAvgText")
                                    : null;
                break;

            case DashboardKnowledgeIndicatorsDetailColumn.UserQt:
                rightColumnName = tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
                    ? <span>Qt {this.res("TopicsText_plural")}</span>
                    : null;
                break;

            case DashboardKnowledgeIndicatorsDetailColumn.TopicQt:
                rightColumnName = tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
                    ? <span>Qt {this.res("UsersText_plural")}</span>
                    : null;
                break;

            case DashboardKnowledgeIndicatorsDetailColumn.UserLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicLevel:
                rightColumnName = tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
                    ? this.res("KnowledgePercText")
                    : tile == DashboardKnowledgeIndicatorsSummaryColumn.Completed
                        ? this.res("CompletedPercText")
                        : tile == DashboardKnowledgeIndicatorsSummaryColumn.Result
                            ? this.res("ResultAvgText")
                            : tile == DashboardKnowledgeIndicatorsSummaryColumn.Trust
                                ? this.res("TrustAvgText")
                                : tile == DashboardKnowledgeIndicatorsSummaryColumn.Revision
                                    ? this.res("RevisionAvgText")
                                    : null;
                break;

            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel:
                rightColumnName = tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
                    ? this.res("KnowledgePercText")
                    : tile == DashboardKnowledgeIndicatorsSummaryColumn.Completed
                        ? this.res("CompletedPercText")
                        : rightColumnName = tile == DashboardKnowledgeIndicatorsSummaryColumn.Result
                            ? this.res("ResultPercText")
                            : tile == DashboardKnowledgeIndicatorsSummaryColumn.Trust
                                ? this.res("TrustPercText")
                                : tile == DashboardKnowledgeIndicatorsSummaryColumn.Revision
                                    ? this.res("RevisionPercText")
                                    : null;
                break;
        }

        return rightColumnName;
    }

    ///
    private renderHeader() {
        return (
            <div id="divList" >
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>

                            <Select
                                value={this.state.primaryListOption}
                                onChange={this.handleListOption}
                            >
                                {
                                    this.getPrimaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                }
                            </Select>

                        </Grid>

                        {
                            (this.state.primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.UserLevel
                                || this.state.primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.UserQt
                                || this.state.primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.TopicLevel
                                || this.state.primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.TopicQt
                                || this.state.primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.UserParticipateLevel
                                || this.state.primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel
                            ) &&
                            <Grid item>
                                <MuiSelectFilter
                                    value={this.state.secondaryListOption}
                                    onChange={this.handleSecondaryListOption}
                                >
                                    {
                                        this.getSecondaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                    }
                                </MuiSelectFilter>
                            </Grid>
                        }
                    </Grid>
                </MuiContainer>
            </div>

        );
    }

    ///
    private renderRightColumnValue(data: DashboardKnowledgeIndicatorsDetailModel) {
        const { tile, t } = this.props;
        const { primaryListOption } = this.state;
        var rightColumnValue = null;
        var drillEnabled = true;

        switch (primaryListOption) {
            case DashboardKnowledgeIndicatorsDetailColumn.UserQt:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicQt:
                rightColumnValue = data.total;
                drillEnabled = data.total != null;
                break;

            case DashboardKnowledgeIndicatorsDetailColumn.UserParticipate:
            case DashboardKnowledgeIndicatorsDetailColumn.UserParticipateLevel:
                rightColumnValue = data.avgCompletedPercent == null
                    ? this.res("NeverAnswered")
                    : data.avgCompletedPercent == 100
                        ? this.res("EverythingFullAnswered")
                        : this.res("DayText", { count: data.total });
                drillEnabled = false;
                break;

            case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
                if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Revision) {
                    rightColumnValue = <span>{data.avgRevisionPercent == null ? "-" : `${data.avgRevisionPercent.toFixed(1)}%`}</span>;
                    drillEnabled = data.avgRevisionPercent != null;
                }
                else if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge) {
                    rightColumnValue = <span>{`${data.avgKnowledgePercent.toFixed(1)}%`}</span>;
                    drillEnabled = data.avgKnowledgePercent != null;
                }
                else if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Result) {
                    rightColumnValue = <span>{data.avgResultPercent?.toFixed(1)}%</span>;
                    drillEnabled = data.avgResultPercent != null;
                }
                else if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Completed) {
                    rightColumnValue = <span>{`${data.avgCompletedPercent?.toFixed(1)}%`}</span>;
                    drillEnabled = data.avgCompletedPercent != null;
                }
                else {
                    rightColumnValue = <span>{data.avgTrustPercent?.toFixed(1)}%</span>;
                    drillEnabled = data.avgTrustPercent != null;
                }
                break;

            default:
                if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Revision) {
                    rightColumnValue = <span>{data.avgRevisionPercent == null ? this.res("NoAnswered") : `${data.avgRevisionPercent.toFixed(1)}%`}</span>;
                    drillEnabled = data.avgRevisionPercent != null;
                }
                else if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge) {
                    rightColumnValue = <span>{`${data.avgKnowledgePercent.toFixed(1)}%`}</span>;
                    drillEnabled = data.avgKnowledgePercent != null;
                }
                else if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Result) {
                    rightColumnValue = <span>{data.avgResultPercent?.toFixed(1)}%</span>;
                    drillEnabled = data.avgResultPercent != null;
                }
                else if (tile == DashboardKnowledgeIndicatorsSummaryColumn.Completed) {
                    rightColumnValue = <span>{`${data.avgCompletedPercent?.toFixed(1)}%`}</span>;
                    drillEnabled = data.avgCompletedPercent != null;
                }
                else {
                    rightColumnValue = <span>{data.avgTrustPercent?.toFixed(1)}%</span>;
                    drillEnabled = data.avgTrustPercent != null;
                }
                break;
        }

        return { value: rightColumnValue, drillEnabled };
    }

    ///
    private renderDetail() {
        const { data, primaryListOption } = this.state;

        return (data.items as DashboardKnowledgeIndicatorsDetailModel[]).map((v, index) => {

            let img = null;
            switch (primaryListOption) {
                case DashboardKnowledgeIndicatorsDetailColumn.UserBetter:
                case DashboardKnowledgeIndicatorsDetailColumn.UserWorst:
                case DashboardKnowledgeIndicatorsDetailColumn.UserLevel:
                case DashboardKnowledgeIndicatorsDetailColumn.UserQt:
                case DashboardKnowledgeIndicatorsDetailColumn.UserParticipate:
                case DashboardKnowledgeIndicatorsDetailColumn.UserParticipateLevel:
                    img = <UserProfileImageLink user={v} />;
                    break;
                case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
                case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
                case DashboardKnowledgeIndicatorsDetailColumn.TopicLevel:
                case DashboardKnowledgeIndicatorsDetailColumn.TopicQt:
                    img = (
                        <MuiLink href={getTopicUrl(v.id)} target="_blank">
                            <img src={v.thumbnailUrl} alt={v.name} width="48" height="48" />
                        </MuiLink>
                    );
                    break;
            }

            let name = null;
            switch (primaryListOption) {
                case DashboardKnowledgeIndicatorsDetailColumn.UserBetter:
                case DashboardKnowledgeIndicatorsDetailColumn.UserWorst:
                case DashboardKnowledgeIndicatorsDetailColumn.UserLevel:
                case DashboardKnowledgeIndicatorsDetailColumn.UserQt:
                case DashboardKnowledgeIndicatorsDetailColumn.UserParticipate:
                case DashboardKnowledgeIndicatorsDetailColumn.UserParticipateLevel:
                    if (this.props.onSelectUser)
                        name = (
                            <MuiLink
                                color="secondary"
                                onClick={() => this.props.onSelectUser(v.id, v.name, v.thumbnailUrl)}
                            >
                                <UsernameInspect user={v} />
                            </MuiLink>
                        );
                    else
                        name = <UsernameInspect user={v} />;
                    break;
                case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
                case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
                case DashboardKnowledgeIndicatorsDetailColumn.TopicLevel:
                case DashboardKnowledgeIndicatorsDetailColumn.TopicQt:
                    if (this.props.onSelectTopic)
                        name = (
                            <MuiLink
                                color="secondary"
                                onClick={() => this.props.onSelectTopic(v.id, v.name, v.thumbnailUrl)}
                            >
                                <UsernameInspect user={v} />
                            </MuiLink>
                        );
                    else
                        name = v.name;
                    break;
            }

            const rightValue = this.renderRightColumnValue(v);

            let secondLine = null;
            switch (primaryListOption) {
                case DashboardKnowledgeIndicatorsDetailColumn.UserBetter:
                case DashboardKnowledgeIndicatorsDetailColumn.UserWorst:
                    if (this.props.tile == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge)
                        secondLine = <span>{v.total} {<TextPluralization count={v.total} resource={`${IndicatorListPrefix}.TopicsText`} />} </span>;
                    break;

                case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
                case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
                    secondLine = <span>{v.total} {<TextPluralization count={v.total} resource={`${IndicatorListPrefix}.UsersText`} />}</span>;
                    break;
            }

            return (
                <TableRow key={index}>
                    <TableCell style={{ width: 1 }}>
                        {img}
                    </TableCell>
                    <TableCell >
                        {name}
                        {
                            secondLine &&
                            <><br /><MuiMuted><small>{secondLine}</small></MuiMuted></>
                        }
                    </TableCell>
                    <TableCell align="right">
                        {
                            rightValue.drillEnabled
                                ? <MuiLink onClick={() => this.drillDown(v.id)}>{rightValue.value}</MuiLink>
                                : rightValue.value
                        }
                    </TableCell>
                </TableRow>
            );
        });
    }

    ///
    private renderUserTopicBetter() {
        const { data } = this.state;

        var d = data as PagedResult<DashboardKnowledgeIndicatorsDataModel>;

        return d.items.map((v, index) => {

            return (
                <TableRow key={index}>
                    <TableCell style={{ width: 1 }} >
                        <img src={v.thumbnailUrl} alt={v.name} style={{ maxWidth: 64 }} />
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>
                        <Grid container spacing={1}>
                            <Grid item>
                                <MuiLink onClick={() => getTopicSecondViewAction(v.topicId)()} target="_blank">
                                    <b>{v.name}</b>
                                </MuiLink>
                                <MuiMuted>
                                    {
                                        v.currentLevel != KnowledgeTestDifficultyLevel.NotInitiate &&
                                        <>
                                            <br />
                                            <KnowlegeDataModelLine summaryColumn={this.props.tile} data={v} />
                                        </>
                                    }
                                </MuiMuted>
                            </Grid>
                        </Grid>
                    </TableCell>
                    <TableCell align="right" style={{ verticalAlign: "top" }}>
                        <KnowledgeTotalColumn summaryColumn={this.props.tile} data={v} />
                    </TableCell>
                </TableRow>
            );
        });
    }

    ///
    private renderList() {
        const { loading, data, dialog, primaryListOption, offset } = this.state;
        const rightColumnName = this.getRightColumnName();

        return (
            <MuiContainer margin="normal">
                <div>
                    {dialog}
                    <br />
                    {this.renderHeader()}

                    {
                        loading
                            ? <SpinnerIndicator />
                            : data == null
                                ? <AppMessageBox resource="LoadListDataFailed" />
                                : data.total == 0
                                    ? <MuiMessageBox variant="info" message={this.getEmptyMessage()} />
                                    : (
                                        <>
                                            <MuiContainer>
                                                <Table size="small">
                                                    {
                                                        rightColumnName &&
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell></TableCell>
                                                                <TableCell></TableCell>
                                                                <TableCell align="right">{rightColumnName}</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                    }
                                                    <TableBody>
                                                        {
                                                            primaryListOption ==
                                                                DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter
                                                                || primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.UserTopicWorst
                                                                || primaryListOption == DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel
                                                                ? this.renderUserTopicBetter()
                                                                : this.renderDetail()
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </MuiContainer>
                                            <MuiPagination
                                                limit={DefaultListParameters.viewRecordsByPage}
                                                offset={offset}
                                                total={data.total}
                                                onClick={(e, offset) => this.loadData(offset)}
                                            />
                                        </>
                                    )
                    }
                </div>
            </MuiContainer >
        );
    }

    ///
    private checkForUpdateState(prevProps: ListProps) {
        if (!LibUtils.isEqual(this.props, prevProps)) {
            this.setState({ ...this.computeListOptions() }, () => this.loadData(0));
        }
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public componentDidUpdate(prevProps: ListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {
        return (
            <div>

                {
                    this.state.loading
                        ? <SpinnerIndicator />
                        : this.renderList()
                }
            </div>
        );
    }
}